import React, { useState } from "react";
import { X, ChevronDown, Calendar as CalendarIcon } from "lucide-react";
import type { SessionTransactionRich } from "./sessionTypes";
import { TRANSACTION_KIND_LABELS } from "./sessionTypes";

interface SessionRowEditProps {
  transaction: SessionTransactionRich;
  onCancel: () => void;
  onSave: (updated: SessionTransactionRich) => void;
  onOpenFullEditor: (id: string) => void;
}

const palette = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  brandDark: "#0F6E56",
};

export function SessionRowEdit({
  transaction,
  onCancel,
  onSave,
  onOpenFullEditor,
}: SessionRowEditProps) {
  const [draft, setDraft] = useState<SessionTransactionRich>(transaction);

  const isPayment = draft.amount < 0;
  const amountLabel = isPayment ? "Payment" : "Deposit";
  const displayAmount = Math.abs(draft.amount);

  const updateAmount = (nextAbs: number) => {
    const positive = Math.abs(nextAbs);
    setDraft({
      ...draft,
      amount: isPayment ? -positive : positive,
    });
  };

  return (
    <div style={styles.wrapper}>
      <button
        type="button"
        onClick={onCancel}
        aria-label="Close inline editor"
        style={styles.closeButton}
      >
        <X size={16} />
      </button>

      <div style={styles.fieldGrid}>
        <Field label="Date">
          <div style={styles.boxedInput}>
            <span>{formatDate(draft.date)}</span>
            <CalendarIcon size={14} color={palette.ink4} />
          </div>
        </Field>

        <Field label="Ref no.">
          <input
            type="text"
            value={draft.refNo ?? ""}
            onChange={(e) => setDraft({ ...draft, refNo: e.target.value })}
            placeholder="To Print"
            style={styles.textInput}
          />
        </Field>

        <Field label="Type">
          <div style={styles.boxedInput}>
            <span>{TRANSACTION_KIND_LABELS[draft.kind]}</span>
            <ChevronDown size={14} color={palette.ink4} />
          </div>
        </Field>

        <Field label="Account">
          <div style={styles.boxedInput}>
            <span style={styles.truncate}>{draft.account}</span>
            <ChevronDown size={14} color={palette.ink4} />
          </div>
        </Field>

        <Field label="Payee">
          <div style={styles.boxedInput}>
            <span style={styles.truncate}>{draft.payee}</span>
            <ChevronDown size={14} color={palette.ink4} />
          </div>
        </Field>

        <Field label="Memo">
          <input
            type="text"
            value={draft.memo ?? ""}
            onChange={(e) => setDraft({ ...draft, memo: e.target.value })}
            style={styles.textInput}
          />
        </Field>

        <Field label={amountLabel}>
          <input
            type="text"
            value={displayAmount.toFixed(2)}
            onChange={(e) => updateAmount(parseFloat(e.target.value) || 0)}
            style={{
              ...styles.textInput,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
            }}
          />
        </Field>
      </div>

      <div style={styles.buttonRow}>
        <button type="button" onClick={onCancel} style={styles.cancelButton}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onOpenFullEditor(draft.id)}
          style={styles.secondaryButton}
        >
          Open full editor →
        </button>
        <button
          type="button"
          onClick={() => onSave(draft)}
          style={styles.saveButton}
        >
          Save
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: "relative",
    padding: "16px 18px",
    background: "#FFFFFF",
    border: `1px solid ${palette.line}`,
    borderRadius: 10,
    margin: "4px 16px 20px",
  },
  closeButton: {
    position: "absolute",
    top: 10,
    right: 12,
    background: "transparent",
    border: 0,
    color: palette.ink4,
    cursor: "pointer",
    padding: 4,
    lineHeight: 1,
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "150px 150px 160px 180px 180px 160px 110px",
    gap: 10,
    alignItems: "end",
  },
  label: {
    fontSize: 11,
    color: palette.ink3,
    fontWeight: 600,
    display: "block",
    marginBottom: 4,
  },
  boxedInput: {
    padding: "8px 12px",
    border: `1px solid ${palette.line}`,
    borderRadius: 8,
    background: "#FFFFFF",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 13,
    color: palette.ink,
    minHeight: 36,
    boxSizing: "border-box",
  },
  textInput: {
    width: "100%",
    padding: "8px 12px",
    border: `1px solid ${palette.line}`,
    borderRadius: 8,
    background: "#FFFFFF",
    fontSize: 13,
    color: palette.ink,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    minHeight: 36,
  },
  truncate: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  buttonRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
  },
  cancelButton: {
    background: "#FFFFFF",
    border: `1px solid ${palette.line}`,
    color: palette.ink2,
    fontWeight: 500,
    fontSize: 13,
    padding: "8px 18px",
    borderRadius: 10,
    cursor: "pointer",
  },
  secondaryButton: {
    background: "#FFFFFF",
    border: `1px solid ${palette.line}`,
    color: palette.ink,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 18px",
    borderRadius: 10,
    cursor: "pointer",
  },
  saveButton: {
    background: palette.brandDark,
    color: "#FFFFFF",
    border: 0,
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 22px",
    borderRadius: 10,
    cursor: "pointer",
  },
};